import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";
import { PERSPECTIVES } from "@/data/perspectives";

const PerspectivesPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.perspectivesPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Perspectives"
          label="Field notes · Research · Industrial capital"
          title={
            <>
              Documented{" "}
              <span className="italic text-[var(--kr-gold)]">thinking</span>{" "}
              from the partnership.
            </>
          }
          description="A small number of considered papers across macro, underwriting and sector themes. Materials are written for sophisticated readers and are not investment advice."
          breadcrumb="Perspectives"
        />

        <section className="py-24 lg:py-32">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 space-y-24">
            {PERSPECTIVES.map((p, idx) => (
              <article
                key={p.slug}
                data-testid={`${LANDING.perspectivesPagePaperPrefix}${idx}`}
                className="grid lg:grid-cols-12 gap-12 lg:gap-16 pb-16 border-b border-[var(--kr-line-soft)] last:border-b-0"
              >
                <div className="lg:col-span-4">
                  <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-4">
                    {p.tag}
                  </div>
                  <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,3vw,2.4rem)] leading-[1.15] mb-6">
                    <Link
                      to={`/perspectives/${p.slug}`}
                      className="hover:text-[var(--kr-gold)] transition-colors"
                    >
                      {p.title}
                    </Link>
                  </h2>
                  <div className="flex items-center gap-6 font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-6">
                    <span>{p.date}</span>
                    <span>{p.minutes}</span>
                  </div>
                  <Link
                    to={`/perspectives/${p.slug}`}
                    className="kr-link font-mono-label text-[11px]"
                  >
                    Read the full paper →
                  </Link>
                </div>
                <div className="lg:col-span-8 lg:pl-8 space-y-6 text-[var(--kr-text-muted)] text-[16px] leading-[1.85] max-w-[68ch]">
                  <p className="text-[var(--kr-text)] text-[17px] italic font-serif-display border-l-2 border-[var(--kr-gold)] pl-6">
                    {p.excerpt}
                  </p>
                  <p>{p.body[0]}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="border-t border-[var(--kr-line)] py-24 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.8vw,2.3rem)] leading-[1.2] max-w-[40ch]">
                Subscribe to the partnership&apos;s{" "}
                <span className="italic text-[var(--kr-gold)]">
                  private notes
                </span>
                .
              </h2>
              <p className="mt-4 text-[var(--kr-text-muted)] text-[15px] leading-[1.75] max-w-[58ch]">
                Periodic, considered correspondence — reserved for eligible
                readers and shared on a documented basis.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-4 lg:justify-end">
              <button
                type="button"
                onClick={openModal}
                data-testid={LANDING.perspectivesPageRequest}
                className="kr-btn-primary"
              >
                Request Subscription
                <span aria-hidden>→</span>
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default PerspectivesPage;
